/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import React from "react";
import { Button } from "baseui/button";
import {
  FiEdit2,
  FiArrowRight,
  FiCopy,
  FiDownload,
  FiTrash2,
} from "react-icons/fi";
import { useDexterTheme } from "../DesignSystem/Theme/ThemeContext";
import { Select, SelectOverrides, Value } from "baseui/select";
import { fileTypeOptionsFilesTable } from "../Utils/Common";
import { ThemeType } from "../DesignSystem/Theme/Theme";

export interface TableSelectActionsBarProps {
  /**
   * Number of selected (checked) items (files and folders). The parent renders
   * this component only when at least one item is selected, so this is >= 1.
   */
  selectedCount: number;
  /** Rename the single selected item. Only shown when exactly one is selected. */
  onRename: () => void;
  /** Move the selected item(s) to a folder. */
  onMoveTo: () => void;
  /** Copy the selected item(s) to a folder. */
  onCopyTo: () => void;
  /** Download the selected item(s). */
  onDownload: () => void;
  /** Delete the selected item(s). */
  onDelete: () => void;
  /**Open file type change modal */
  onFileTypeChange: (value: Value) => void;
}

/**
 * Every action is injected by the parent.
 */
export function TableSelectActionsBar({
  selectedCount,
  onRename,
  onMoveTo,
  onCopyTo,
  onDownload,
  onDelete,
  onFileTypeChange,
}: TableSelectActionsBarProps): JSX.Element {
  const [extension, _setExtension] = React.useState<Value>([]);

  const { currentTheme } = useDexterTheme();
  const singleSelected = selectedCount === 1;

  const SELECT_OVERRIDES = (theme: ThemeType): SelectOverrides => ({
    Root: {
      style: {
        cursor: "pointer",
        fontFamily: theme.fonts.primary,
      },
    },
    ControlContainer: {
      style: ({
        $isFocused,
        $isOpen,
        $disabled,
      }: {
        $isFocused?: boolean;
        $isOpen?: boolean;
        $disabled?: boolean;
      }) => ({
        backgroundColor: theme.colors.backgroundTertiary,
        borderTopWidth: "1px",
        borderRightWidth: "1px",
        borderBottomWidth: "1px",
        borderLeftWidth: "1px",
        borderTopStyle: "solid",
        borderRightStyle: "solid",
        borderBottomStyle: "solid",
        borderLeftStyle: "solid",
        borderTopColor:
          $isFocused || $isOpen
            ? theme.colors.text
            : theme.colors.dropdownBorderColor,
        borderRightColor:
          $isFocused || $isOpen
            ? theme.colors.text
            : theme.colors.dropdownBorderColor,
        borderBottomColor:
          $isFocused || $isOpen
            ? theme.colors.text
            : theme.colors.dropdownBorderColor,
        borderLeftColor:
          $isFocused || $isOpen
            ? theme.colors.text
            : theme.colors.dropdownBorderColor,
        borderTopLeftRadius: "8px",
        borderTopRightRadius: "8px",
        borderBottomLeftRadius: "8px",
        borderBottomRightRadius: "8px",
        opacity: $disabled ? 0.5 : 1,
        cursor: $disabled ? "not-allowed" : "pointer",
      }),
    },
    // Empty state text
    Placeholder: {
      style: {
        color: theme.colors.text,
        opacity: 0.6,
        fontFamily: theme.fonts.primary,
        fontSize: theme.fontSizes.medium,
      },
    },
    // Selected value text
    SingleValue: {
      style: {
        color: theme.colors.text,
        fontFamily: theme.fonts.primary,
        fontSize: theme.fontSizes.medium,
      },
    },
    // Arrow and clear (×) icons
    SelectArrow: {
      style: {
        color: theme.colors.text,
      },
    },
    ClearIcon: {
      style: {
        color: theme.colors.text,
        opacity: 0.7,
      },
    },
  });

  const actions = [
    // Rename: any single item (file or folder).
    ...(singleSelected
      ? [
          {
            key: "rename",
            icon: <FiEdit2 size={16} />,
            label: "Rename",
            onClick: onRename,
          },
        ]
      : []),
    {
      key: "move",
      icon: <FiArrowRight size={16} />,
      label: "Move to",
      onClick: onMoveTo,
    },
    {
      key: "copy",
      icon: <FiCopy size={16} />,
      label: "Copy to",
      onClick: onCopyTo,
    },
    {
      key: "download",
      icon: <FiDownload size={16} />,
      label: "Download",
      onClick: onDownload,
    },
    {
      key: "delete",
      icon: <FiTrash2 size={16} />,
      label: "Delete",
      onClick: onDelete,
    },
  ];

  const actionButtonOverrides = {
    BaseButton: {
      style: {
        backgroundColor: "transparent",
        // Button label + icon color for this bar. Icons inherit currentColor,
        // so this one token drives both.
        color: currentTheme.colors.tableSelectActionColor,
        fontFamily: currentTheme.fonts.primary,
        fontSize: currentTheme.fontSizes.medium,
        paddingTop: "8px",
        paddingBottom: "8px",
        paddingLeft: "12px",
        paddingRight: "12px",
        ":hover": {
          backgroundColor: currentTheme.colors.dropdownItemHoverBackground,
        },
      },
    },
  };

  return (
    <>
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          paddingLeft: "8px",
          color: currentTheme.colors.text,
          fontFamily: currentTheme.fonts.primary,
          fontSize: currentTheme.fontSizes.medium,
          fontWeight: 600,
          whiteSpace: "nowrap",
        }}
      >
        {`${selectedCount} selected`}
      </span>

      <div
        style={{
          width: "1px",
          height: "24px",
          margin: "0 4px",
          backgroundColor: currentTheme.colors.dropdownBorderColor,
          flexShrink: 0,
        }}
      />

      {actions.map((action) => (
        <Button
          key={action.key}
          kind="tertiary"
          onClick={action.onClick}
          overrides={actionButtonOverrides}
        >
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              whiteSpace: "nowrap",
            }}
          >
            {action.icon} {action.label}
          </span>
        </Button>
      ))}

      <div
        style={{
          marginLeft: "auto",
          display: "flex",
          alignItems: "center",
          gap: "12px",
          paddingLeft: "24px",
          flexShrink: 0,
        }}
      >
        <span
          style={{
            color: currentTheme.colors.text,
            fontFamily: currentTheme.fonts.primary,
            fontSize: currentTheme.fontSizes.medium,
            whiteSpace: "nowrap",
          }}
        >
          Apply to all:
        </span>
        <div style={{ width: "350px" }}>
          <Select
            overrides={SELECT_OVERRIDES(currentTheme)}
            options={fileTypeOptionsFilesTable}
            value={extension}
            placeholder="Change file extension..."
            searchable={false}
            clearable
            onChange={({ value }) => onFileTypeChange(value)}
          />
        </div>
      </div>
    </>
  );
}

export default TableSelectActionsBar;
